import {
  PromiseBlotterLookupService,
  BaseTableDefinitionFactory,
  TableDefinition,
  TableModelObject,
} from "shared/cubiq-services";
import { Subject } from "rxjs";
import { BLOTTER_RELOAD_TYPE } from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/**
 * if any subclass constructor side effects are required before initialization runs pass this to AbstractBaseBlotterService or relevent superclass
 */
export const SKIP_INIT_DATA_SERVICE = new Proxy<PromiseBlotterLookupService<any, any>>({} as any, {
  get: (_, property) => {
    logger.out.warn(
      `AbstractBaseBlotterService.dataService.${property.toString()} stub hit. please re-write to use active services`,
    );
  },
});

abstract class AbstractBaseBlotterService {
  protected _readyResFn;
  protected _readyRejFn;

  protected _ready: Promise<void> = new Promise<void>((res, rej) => {
    this._readyResFn = res;
    this._readyRejFn = rej;
  });

  protected _rawData: NullProtoObject = Object.create(null);
  protected _tableDefinition: TableDefinition;

  protected _queuedFullUpdate: Array<Array<TableModelObject>> = [];
  protected _queuedDeleteItem: Array<string> = [];
  protected _queueFlushedAfterPrime = false;

  public blotterChangeSubject: Subject<BLOTTER_RELOAD_TYPE> = new Subject<BLOTTER_RELOAD_TYPE>();
  public fullUpdateSubject: Subject<Array<any>> = new Subject<Array<any>>();
  public forceUpdateSubject: Subject<Array<any>> = new Subject<Array<any>>();
  public deleteItemSubject: Subject<any> = new Subject<any>();

  constructor(
    public dataService: PromiseBlotterLookupService<any, any>,
    public tableDefinitionFactory: BaseTableDefinitionFactory,
  ) {
    if (this.skipInit(dataService)) return this;

    this.createTableDefinition();
    this.primeDataService();
  }

  protected skipInit(dataService: PromiseBlotterLookupService<any, any>): boolean {
    return dataService === SKIP_INIT_DATA_SERVICE;
  }

  protected abstract createTableDefinition(): void;
  protected abstract primeDataService(): void;
  protected abstract initData(primed: boolean): void;
  protected abstract initDataRows(): void;

  /**
   * subscribe to data service updates.
   */
  protected abstract subscribeToSubject(): void;

  /**
   * return if object reference used is changed
   */
  protected addOrUpdateRow(row: any): any {
    let rowId: string;

    if (
      row[this._tableDefinition.primaryKey] != null &&
      typeof row[this._tableDefinition.primaryKey] != "string"
    ) {
      rowId = row[this._tableDefinition.primaryKey].toString();
    } else {
      rowId = row[this._tableDefinition.primaryKey];
    }

    this._rawData[rowId] = row;

    return null;
  }

  protected flushQueuedSubjects(): void {
    if (
      Array.isArray(this._queuedFullUpdate) === false ||
      Array.isArray(this._queuedDeleteItem) === false
    )
      return;

    this._queuedFullUpdate.forEach((rows: TableModelObject[]) => {
      this.onUpdateEvent(rows);
    });

    this._queuedDeleteItem.forEach((id: string) => {
      this.onDeleteEvent(id);
    });

    this._queuedFullUpdate = [];
    this._queuedDeleteItem = [];
    this._queueFlushedAfterPrime = true;
  }

  protected onModelDelete(id) {
    if (this._queueFlushedAfterPrime) {
      this.onDeleteEvent(id);
    } else {
      this._queuedDeleteItem.push(id);
    }
  }

  protected onModelUpdate(data) {
    if (this._queueFlushedAfterPrime) {
      this.onUpdateEvent(data);
    } else {
      this._queuedFullUpdate.push([data]);
    }
  }

  protected onInitialPrime(rows: Array<any>): void {
    for (let i = 0, len = rows.length; i < len; i++) {
      this.addOrUpdateRow(rows[i]);
    }
  }

  protected onUpdateEvent(row: any): void {
    if (!Array.isArray(row)) this.onFullUpdateEvent([row]);
    else this.onFullUpdateEvent(row);
  }

  protected onFullUpdateEvent(rows: Array<any>, forceRepaint: boolean = false): void {
    for (let i = 0, len = rows.length; i < len; i++) {
      this.addOrUpdateRow(rows[i]);
    }
    if (forceRepaint) {
      this.forceUpdateSubject.next(rows);
    } else {
      this.fullUpdateSubject.next(rows);
    }
  }

  protected onDeleteEvent(eventId: string | number | CubiqTS.Long): void {
    let rowId: string;
    if (eventId != null && typeof eventId != "string") {
      rowId = eventId.toString();
    } else {
      rowId = eventId as string;
    }

    delete this._rawData[rowId];

    this.deleteItemSubject.next(rowId);
  }

  public isPrimed(): Promise<boolean> {
    return new Promise((res, rej) => {
      this._ready.then(
        () => {
          res(true);
        },
        () => {
          res(false);
        },
      );
    });
  }

  public isReady(): Promise<void> {
    return this._ready;
  }

  public getRowData(): Array<any> {
    let ret: Array<any> = null;
    ret = Object.values(this._rawData);
    return ret;
  }

  /**
   * Get an array of rowData objects for the input row IDs - the IDs are always
   * converted to String before lookup
   * @param ids
   * @returns
   */
  public getRowDataForIdList(ids: Array<any>): Array<any> {
    let retRows: Array<any> = [];

    ids.forEach((key) => {
      if (key) {
        const keyStr = key.toString();
        const item = this._rawData[keyStr];
        if (item) {
          retRows.push(item);
        }
      }
    });
    return retRows;
  }

  /**
   * Get a single rowData objects for the input row ID
   * @param ids
   * @returns
   */
  public getRowDataForId(id: string): any {
    return this._rawData[id];
  }

  public getRowDataForIds(ids: Set<string>): Array<any> {
    let updates: Array<any> = [];

    ids.forEach((key: string) => {
      let item = this._rawData[key];
      if (item) {
        item.tempStale = true;
        updates.push(item);
      }
    });
    return updates;
  }

  public getRawDataObject(): any {
    return this._rawData;
  }

  public getTableDefinition(): TableDefinition {
    return this._tableDefinition;
  }

  protected resolvePriming(res, rej): void {
    res();
  }

  protected rejectPriming(res, rej, error): void {
    rej();
  }
}

/**
 * BfxBaseBlotter is for blotters with a single dataService.
 *
 */
export abstract class BfxBaseBlotterService extends AbstractBaseBlotterService {
  protected createTableDefinition(): void {
    this._tableDefinition = this.tableDefinitionFactory.createDef();
  }

  protected primeDataService(): void {
    if (this.dataService != null) {
      this.dataService.isPrimed().then(
        (primed) => this.initData(primed),
        (error) => {
          this.rejectPriming(this._readyResFn, this._readyRejFn, error);
        },
      );
    } else {
      logger.out.error(`${this.dataService} is null`);
    }
  }

  protected initData(primed: boolean): void {
    if (!primed) {
      this.rejectPriming(
        this._readyResFn,
        this._readyRejFn,
        Error(`${this.constructor.name}.dataService.isPrimed() returned false`),
      );
      return;
    }
    this.subscribeToSubject();
    this.initDataRows();
  }

  protected initDataRows(): void {
    this.dataService.getAll().then(
      (rows: any) => {
        this.onInitialPrime(rows);
        this.flushQueuedSubjects();
        this.resolvePriming(this._readyResFn, this._readyRejFn);
      },
      (error) => {
        this.rejectPriming(this._readyResFn, this._readyRejFn, error);
      },
    );
  }

  protected subscribeToSubject(): void {
    this.dataService.getModelUpdateSubject().subscribe((data) => this.onModelUpdate(data));
    this.dataService.getModelDeleteSubject().subscribe((data) => this.onModelDelete(data));
  }
}

/**
 * BfxBaseBlotter is for blotters with a single dataService.
 *
 */
export abstract class BfxMultiBlotterService extends BfxBaseBlotterService {
  protected abstract runOnActiveDataService(
    methodName: string,
    ...args
  ): Promise<PromiseSettledResult<any>[]>;

  protected primeDataService() {
    this.runOnActiveDataService("isPrimed").then(
      (active) => {
        const primed = active.every((res) => res?.status === "fulfilled" && res?.value);
        this.initData(primed);
      },
      (error) => {
        logger.out.error(`${this.constructor.name}.primeDataService() %o`, error);
        this.initData(false);
      },
    );
  }

  protected initDataRows() {
    this.runOnActiveDataService("getAll").then(
      (results) => {
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            this.onInitialPrime(result.value);
          }
        });
        this.flushQueuedSubjects();
        this.resolvePriming(this._readyResFn, this._readyRejFn);
      },
      (error) => {
        this.rejectPriming(this._readyResFn, this._readyRejFn, error);
      },
    );
  }
}
